import { supabase } from './supabaseClient';

export interface GoalTemplate {
  id: string;
  facilityId?: string;
  title: string;
  description?: string;
  categoryId?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export class GoalService {
  /**
   * Get all active goal templates for a facility
   */
  static async getGoalTemplates(facilityId?: string): Promise<GoalTemplate[]> {
    console.log('🔍 Fetching goal templates for facility:', facilityId || 'global');
    
    let query = supabase
      .from('goal_templates')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (facilityId) {
      // Get facility-specific templates and global templates
      query = query.or(`facility_id.eq.${facilityId},facility_id.is.null`);
    } else {
      // Get only global templates
      query = query.is('facility_id', null);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Error fetching goal templates:', error);
      throw error;
    }

    console.log(`✅ Retrieved ${data?.length || 0} goal templates`);
    return (data || []).map(this.mapDbToGoalTemplate);
  }

  /**
   * Create a new goal template
   */
  static async createGoalTemplate(template: Omit<GoalTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<GoalTemplate> {
    console.log('🆕 Creating new goal template:', template);
    
    const { data, error } = await supabase
      .from('goal_templates')
      .insert([{
        facility_id: template.facilityId,
        title: template.title,
        description: template.description,
        category_id: template.categoryId,
        is_active: template.isActive,
        sort_order: template.sortOrder
      }])
      .select()
      .single();

    if (error) {
      console.error('❌ Error creating goal template:', error);
      throw error;
    }

    console.log('✅ Goal template created successfully');
    return this.mapDbToGoalTemplate(data);
  }

  /**
   * Update an existing goal template
   */
  static async updateGoalTemplate(
    id: string, 
    updates: Partial<Omit<GoalTemplate, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<GoalTemplate> {
    console.log('📝 Updating goal template:', id, updates);
    
    const updateData: any = {};
    if (updates.facilityId !== undefined) updateData.facility_id = updates.facilityId;
    if (updates.title) updateData.title = updates.title;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.categoryId !== undefined) updateData.category_id = updates.categoryId;
    if (updates.isActive !== undefined) updateData.is_active = updates.isActive;
    if (updates.sortOrder !== undefined) updateData.sort_order = updates.sortOrder;

    const { data, error } = await supabase
      .from('goal_templates')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating goal template:', error);
      throw error;
    }

    console.log('✅ Goal template updated successfully');
    return this.mapDbToGoalTemplate(data);
  }

  /**
   * Delete a goal template
   */
  static async deleteGoalTemplate(id: string): Promise<void> {
    console.log('🗑️ Deleting goal template:', id);
    
    const { error } = await supabase
      .from('goal_templates')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('❌ Error deleting goal template:', error);
      throw error;
    }

    console.log('✅ Goal template deleted successfully');
  }

  /**
   * Get next available sort order for goal templates
   */
  static async getNextTemplateSortOrder(): Promise<number> {
    const { data, error } = await supabase
      .from('goal_templates')
      .select('sort_order')
      .order('sort_order', { ascending: false })
      .limit(1);

    if (error || !data || data.length === 0) {
      return 10; // Default starting sort order
    }

    return (data[0].sort_order || 0) + 10;
  }

  // ==================== HELPER METHODS ====================

  /**
   * Map database row to GoalTemplate
   */
  private static mapDbToGoalTemplate(row: any): GoalTemplate {
    return {
      id: row.id || '',
      facilityId: row.facility_id || undefined,
      title: row.title || '',
      description: row.description || undefined,
      categoryId: row.category_id || undefined,
      isActive: row.is_active !== false,
      sortOrder: row.sort_order || 0,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }
}

